import { useCallback, useContext } from 'react';
import { change } from '../../components/connectedForm/utils/actions';
import { autoDeallocate, autoUnreserve } from '../../utils/AllocationUtil';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import {
  IAllocationSalesHelper,
  IAllocationSalesHelperContainer,
  IAllocationSalesHelperContainerFragment,
  IManualAutoAllocateMutationResult,
  IManualAutoAllocateMutationVariables,
  IManualAutoReserveMutationResult,
  IManualAutoReserveMutationVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import { useMutation } from '@apollo/client';
import ManualAutoAllocateMutation from '../../graphql/mutations/product/ManualAutoAllocateMutation.graphql';
import ManualAutoReserveMutation from '../../graphql/mutations/product/ManualAutoReserveMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables, { normalizeFields } from '../../graphql/schema/normalizeVariables';

export const formActions = [
  {
    actionReference: 'AUTO_ALLOCATE',
  },
  {
    actionReference: 'AUTO_RESERVE',
  },
];

export const useAutoAllocateAndReserveActions = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const [manualAutoAllocate] = useMutation<IManualAutoAllocateMutationResult, IManualAutoAllocateMutationVariables>(
    ManualAutoAllocateMutation
  );
  const [manualAutoReserve] = useMutation<IManualAutoReserveMutationResult, IManualAutoReserveMutationVariables>(
    ManualAutoReserveMutation
  );

  const values: Maybe<{ __typename?: 'AllocationSalesHelperContainer' } & IAllocationSalesHelperContainerFragment> =
    connectedForm.values;

  const autoAllocateClick = useCallback(
    (rowIndex?: number) => {
      let allocationSalesHelpers: Array<Maybe<IAllocationSalesHelper>>;
      let path: string;
      if (typeof rowIndex === 'number') {
        allocationSalesHelpers = [(values as IAllocationSalesHelperContainer)!.allocationSalesHelper![rowIndex]];
        path = `allocationSalesHelper.${rowIndex}`;
      } else {
        allocationSalesHelpers = (values as IAllocationSalesHelperContainer)!.allocationSalesHelper!;
        path = 'allocationSalesHelper';
      }

      manualAutoAllocate({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            allocationSalesHelpers: normalizeVariables(allocationSalesHelpers, [
              ...normalizeFields,
              'binLocation',
              'lotAttributes',
            ]),
          },
        },
      }).then((value) => {
        const result =
          typeof rowIndex === 'number'
            ? value.data?.manualAutoAllocate?.result?.[0]
            : value.data?.manualAutoAllocate?.result;

        dispatch(change('AllocationSalesHelperContainer', path, result));
      });
    },
    [values!.allocationSalesHelper, manualAutoAllocate]
  );

  const autoReserveClick = useCallback(
    (rowIndex?: number) => {
      let allocationSalesHelpers: Array<Maybe<IAllocationSalesHelper>>;
      let path: string;
      if (typeof rowIndex === 'number') {
        allocationSalesHelpers = [(values as IAllocationSalesHelperContainer)!.allocationSalesHelper![rowIndex]];
        path = `allocationSalesHelper.${rowIndex}`;
      } else {
        allocationSalesHelpers = (values as IAllocationSalesHelperContainer)!.allocationSalesHelper!;
        path = 'allocationSalesHelper';
      }

      manualAutoReserve({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            allocationSalesHelpers: normalizeVariables(allocationSalesHelpers, [
              ...normalizeFields,
              'binLocation',
              'lotAttributes',
            ]),
          },
        },
      }).then((value) => {
        const result =
          typeof rowIndex === 'number'
            ? value.data?.manualAutoReserve?.result?.[0]
            : value.data?.manualAutoReserve?.result;

        dispatch(change('AllocationSalesHelperContainer', path, result));
      });
    },
    [values!.allocationSalesHelper, manualAutoReserve]
  );

  return [autoAllocateClick, autoReserveClick];
};

export const useAllocationActions = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const [autoAllocateClick, autoReserveClick] = useAutoAllocateAndReserveActions();

  const values: Maybe<{ __typename?: 'AllocationSalesHelperContainer' } & IAllocationSalesHelperContainerFragment> =
    connectedForm.values;

  //Just making sure we don't pass an unexpected argument that could result in only a single line being allocated.
  const noArgumentAutoAllocateClick = useCallback(() => {
    return autoAllocateClick();
  }, [autoAllocateClick]);

  //Just making sure we don't pass an unexpected argument that could result in only a single line being reserved.
  const noArgumentAutoReserveClick = useCallback(() => {
    return autoReserveClick();
  }, [autoReserveClick]);

  const autoAllocateAction = {
    key: 'autoAllocate',
    text: 'Auto Allocate',
    onClick: noArgumentAutoAllocateClick,
    actionReference: 'AUTO_ALLOCATE',
  };

  const autoDeallocateClick = () => {
    const newAllocationSalesHelperContainer = autoDeallocate(
      values?.allocationSalesHelper as Array<IAllocationSalesHelper>
    );

    dispatch(change('AllocationSalesHelperContainer', 'allocationSalesHelper', newAllocationSalesHelperContainer));
  };

  const autoDeallocateAction = {
    key: 'autoDeallocate',
    text: 'Auto Deallocate',
    onClick: autoDeallocateClick,
    actionReference: 'AUTO_ALLOCATE',
  };

  const autoReserveAction = {
    key: 'autoReserve',
    text: 'Auto Reserve',
    onClick: noArgumentAutoReserveClick,
    actionReference: 'AUTO_RESERVE',
  };

  const autoUnreserveClick = () => {
    const newAllocationSalesHelperContainer = autoUnreserve(
      values?.allocationSalesHelper as Array<IAllocationSalesHelper>
    );

    dispatch(change('AllocationSalesHelperContainer', 'allocationSalesHelper', newAllocationSalesHelperContainer));
  };

  const autoUnreserveAction = {
    key: 'autoUnreserve',
    text: 'Auto Unreserve',
    onClick: autoUnreserveClick,
    actionReference: 'AUTO_RESERVE',
  };

  return [autoAllocateAction, autoDeallocateAction, autoReserveAction, autoUnreserveAction];
};
